<script>
import {
  LOADING_KEY,
  VENUE_REPORTS_DATE_FILTER_DEFAULT, VENUE_SALES_DATE_FILTER_DEFAULT
} from '@/util/constants';
import { mapActions, mapState, mapGetters } from 'vuex';
import EzTable from '@/components/ui/Table';
import EzFilterList from '@/components/ui/FilterList';
import VDatePicker from '@/components/v3/patterns/VDatePicker/index';
import EzInput from '@/components/ui/Input';
import EzButton from '@/components/ui/Button';
import EzSpinner from '@/components/ui/Spinner/EzSpinner';
import EmptyState from '@/views/common/empty-state';
import EzLoader from '@/components/ui/Loader/EzLoader';
import EzLoadMore from '@/components/ui/LoadMore';
import { debounce } from '@/util/utils';
import downloadAttachment from '@/util/downloadAttachment';
import dayjs from 'dayjs';
import flash from "@/components/ui/FlashMessage";
import { EzConfirmationModal } from "@/components/ui/Modal";
import VAccountOwnerEntityInfo from "@/components/v3/patterns/VAccountOwnerEntityInfo";
import EzCsvUploadModal from "@/components/ui/Modal/EzCsvUploadModal";

const FilterArea = {
  List: 'list',
};

export default {
  components: {
    EzTable,
    EzFilterList,
    VDatePicker,
    EzInput,
    EzButton,
    EzSpinner,
    EmptyState,
    EzLoader,
    EzLoadMore,
    EzConfirmationModal,
    VAccountOwnerEntityInfo,
    EzCsvUploadModal,
  },
  props: {
    from: {
      required: false,
      default: null,
    },
    to: {
      required: false,
      default: null,
    },
  },
  data() {
    return {
      sales: [],
      meta: {},
      FilterArea,
      listFilters: {
        term: null,
      },
      range: {
        start: VENUE_SALES_DATE_FILTER_DEFAULT.from,
        end: VENUE_SALES_DATE_FILTER_DEFAULT.to,
      },
      defaultRangeName: VENUE_SALES_DATE_FILTER_DEFAULT.name,
      selectedForDelete: null,
    };
  },
  computed: {
    ...mapState('entities/users', ['context', 'contextId', 'loggedUser']),
    ...mapGetters('loading', ['getLoading']),
    groupViewMode() {
      if (this.loggedUser.context === 'venue-group') return true;
      return false;
    },
    isLoadingMore() {
      return (
        this.getLoading(LOADING_KEY.VENUE_FETCH_MORE_SALE_UPLOADS)
      );
    },
    isLoading() {
      return (
        this.getLoading(LOADING_KEY.VENUE_FETCH_SALE_UPLOADS) ||
        this.getLoading(LOADING_KEY.VENUE_FETCH_MORE_SALE_UPLOADS)
      );
    },
    filters() {
      return { ...this.listFilters };
    },
    hasFilters() {
      return Object.entries(this.filters).reduce(
        (prevFilter, [, value]) => prevFilter || !!value,
        false,
      );
    },
    isNotAllTime() {
      // If default range name is 'All Time'
      return !!this.defaultRangeName && this.defaultRangeName !== 'All Time';
    },
  },
  methods: {
    resetDrawerRoute() {
      this.$router.replace({ name: 'venue-reports-sales' });
    },
    ...mapActions('entities/venues', [
      'venueFetchSaleUploads',
      'venueExportSaleUpload',
      'venueDeleteSaleUpload',
      'venueImportSalesCSV',
      'venueDownloadSalesCSVTemplate',
    ]),
    async fetchSaleUploads(
      query = {},
      loadingKey = LOADING_KEY.VENUE_FETCH_SALE_UPLOADS,
    ) {
      const { data } = await this.venueFetchSaleUploads({
        page: this.meta.page || 1,
        limit: 10,
        ...query,
      });
      if (loadingKey === LOADING_KEY.VENUE_FETCH_SALE_UPLOADS) {
        this.sales = data.data;
      } else {
        this.sales = [...this.sales, ...data.data];
      }
      this.meta = data.meta;
    },
    async onLoadMoreClick() {
      await this.fetchSaleUploads(
        {
          page: (this.meta.page || 0) + 1,
          ...this.filters,
          ...(this.isNotAllTime && { from: dayjs(this.listFilters.range?.from).valueOf() }),
          ...(this.isNotAllTime && { to: dayjs(this.listFilters.range?.to).valueOf() }),
        },
        LOADING_KEY.VENUE_FETCH_MORE_SALE_UPLOADS,
      );
    },
    showSalesDataUpload() {
      this.$refs.csvUploadModal.open();
    },
    async uploadSalesCSV(formData, dateParams = {}) {
      this.listFilters.range = {
        from: dateParams.from,
        to: dateParams.to,
      };
      this.defaultRangeName = dateParams.defaultRangeName;
      this.range = {
        start: dateParams.from,
        end: dateParams.to,
      };
      return this.venueImportSalesCSV({
        formData,
        ...dateParams.from && { from: dayjs(dateParams.from).format('YYYY-MM-DD') },
        ...dateParams.to && { to: dayjs(dateParams.to).format('YYYY-MM-DD') },
      });
    },
    async onUploadFinished() {
      this.sales = [];
      this.meta = {};
      await this.fetchSaleUploads({
        ...this.filters,
        ...(this.isNotAllTime && { from: dayjs(this.listFilters.range?.from).format('YYYY-MM-DD') }),
        ...(this.isNotAllTime && { to: dayjs(this.listFilters.range?.to).format('YYYY-MM-DD') }),
      });
    },
    async downloadSalesTemplate() {
      try {
        const { data } = await this.venueDownloadSalesCSVTemplate();
        downloadAttachment(data.data.url, 'sales-import-template.csv');
      } catch (e) {
        console.error('Error downloading template:', e);
      }
    },
    changeRangeName(rangeName) {
      this.defaultRangeName = rangeName;
    },
    updateFilters: debounce(async function deb(area, event) {
      if (area === FilterArea.List) {
        const [filterName, value] = event;
        if (filterName === 'date') {
          this.listFilters = {
            ...this.listFilters,
            ...(this.isNotAllTime
              ? {
                  range: {
                    from: dayjs(this.range?.start).valueOf(),
                    to: dayjs(this.range?.end).valueOf(),
                  },
                }
              : { range: null }),
          };
        } else {
          this.listFilters = { ...this.listFilters, [filterName]: value };
        }
      }

      this.sales = [];
      this.meta = {};
      await this.fetchSaleUploads({
        ...this.filters,
        ...(this.isNotAllTime && { from: dayjs(this.listFilters.range?.from).format('YYYY-MM-DD') }),
        ...(this.isNotAllTime && { to: dayjs(this.listFilters.range?.to).format('YYYY-MM-DD') }),
      });
    }, 300),
    async resetFilters(area) {
      if (area === FilterArea.List) {
        this.listFilters = { term: null };
        this.range = {
          start: VENUE_REPORTS_DATE_FILTER_DEFAULT.from,
          end: VENUE_REPORTS_DATE_FILTER_DEFAULT.to,
        };
        this.defaultRangeName = 'All Time';
      }

      this.$nextTick(() => this.$refs.filtersGroup.syncLocalFilters());

      this.sales = [];
      this.meta = {};
      await this.fetchSaleUploads(this.filters);
    },
    async exportCSV(id) {
      const { data, headers } = await this.venueExportSaleUpload({
       uploadId: id,
      });
      const filename = (headers['content-disposition'] || '').match(/filename="(.+)"/);
      const name = Array.isArray(filename) ? decodeURI(filename[1]) : 'sale-upload.csv';
      downloadAttachment(data, name);
    },
    onRemove(sale) {
      this.selectedForDelete = sale;
      this.openDeleteModal();
    },
    openDeleteModal() {
      this.$refs.deleteModal.open();
    },
    closeDeleteModal() {
      this.$refs.deleteModal.close();
      this.selectedForDelete = null;
    },
    async deleteSaleUpload() {
      try {
        const { id } = this.selectedForDelete;
        await this.venueDeleteSaleUpload({
          uploadId: id,
        });
        await this.fetchSaleUploads({
          ...this.filters,
          ...(this.isNotAllTime && { from: dayjs(this.listFilters.range?.from).format('YYYY-MM-DD') }),
          ...(this.isNotAllTime && { to: dayjs(this.listFilters.range?.to).format('YYYY-MM-DD') }),
        });
        this.closeDeleteModal();
        flash.success({
          title: 'Upload successfully deleted!',
        });
      } catch (e) {
        flash.error({
          title: 'Something went wrong!',
        });
      }
    },
  },
  async created() {
    await Promise.all([
      this.fetchSaleUploads({
        ...this.filters,
        ...(this.isNotAllTime && { from: dayjs(this.dropdownFilters.range?.from).format('YYYY-MM-DD') }),
        ...(this.isNotAllTime && { to: dayjs(this.dropdownFilters.range?.to).format('YYYY-MM-DD') }),
      }),
    ]);
  },
  watch: {
    filters: {
      immediate: true,
    },
  },
};
</script>

<template>
  <div>
    <div class="all-products__actions mt-24">
      <ez-filter-list
        :filters="listFilters"
        @filterUpdated="(...$event) => updateFilters(FilterArea.List, $event)"
        @resetFilter="resetFilters(FilterArea.List)"
        class="mr-16"
      >
        <ez-input
          formKey="filters"
          label="search"
          name="term"
          class="search"
          placeholder="Search for a File"
        >
          <template #prefix>
            <font-awesome-icon icon="search" />
          </template>
        </ez-input>
        <v-date-picker
          class="calendar ml-16"
          name="date"
          label="Date"
          v-model="range"
          hide-label
          formKey="filters"
          range-mode
          with-predefined-ranges
          :number-of-calendars="2"
          :select-predefined-range="defaultRangeName"
          @rangeNameChange="changeRangeName"
          displayEndOfRange
        />
      </ez-filter-list>
      <div class="export-actions">
        <ez-button @click="showSalesDataUpload" type="primary">
          Add Sales
        </ez-button>
      </div>

    </div>
    <ez-table
      class="mt-24"
      v-if="sales.length && !isLoading"
      :headers="{
        date: () => 'Sale Period',
        filename: () => 'Filename',
        user: () => 'Uploaded By',
        createdAt: () => 'Upload Date',
        download: () => 'Actions'
      }"
      :columns="[
        'date',
        'filename',
        'user',
        'createdAt',
        'download',
      ]"
      :columnProps="{
       date: { class: 'large-cell' },
       filename: { class: 'large-cell' },
       user: { class: 'medium-cell' },
       createdAt: { class: 'date-cell medium-cell' },
       download: { class: 'medium-cell' },
      }"
      :data="sales"
    >
      <template #cell-date="{ row }">
        <span>{{ row.date}}</span>
      </template>
      <template #cell-filename="{ row }">
        <span>{{ row.filename}}</span>
      </template>
      <template #cell-createdAt="{ row }">
        <span>{{ row.createdAt | date }}</span>
      </template>
      <template #cell-user="{ row: { user } }">
        <v-account-owner-entity-info
          :account-owner="user"
          :to-show-name="false"
          :tooltip-content="`${user.name}`"
        />
      </template>
      <template #cell-download="{ row }">
<!--        <img-->
<!--          src="@/assets/icons/ic_export-ready.svg"-->
<!--          alt="Download"-->
<!--          @click.stop="exportCSV(row.id)"-->
<!--        />-->
        <ez-button @click="exportCSV(row.id)" type="link">
          <font-awesome-icon icon="download" />
        </ez-button>
        <ez-button @click="onRemove(row)" type="link">
          <font-awesome-icon icon="trash" />
        </ez-button>
      </template>
    </ez-table>
    <template v-if="!sales.length">
      <empty-state class="empty-state-center" v-if="hasFilters">
        <template #badge><img src="@/assets/no-products.svg" alt="" /></template>
        <template #title>No sales match this search</template>
        <template #info>Try with a different search.</template>
      </empty-state>
      <empty-state class="empty-state-center" v-else>
        <template #badge><img src="@/assets/no-products.svg" alt="" /></template>
        <template #title>No sales uploaded</template>
        <!-- eslint-disable-next-line -->
      </empty-state>
    </template>
    <div v-if="isLoadingMore" class="u-text-center mt-12">
      <ez-spinner />
    </div>
    <ez-loader :show="isLoading">Loading...</ez-loader>
    <ez-load-more v-if="meta.nextId && !isLoadingMore" @loadMore="onLoadMoreClick"></ez-load-more>
    <ez-confirmation-modal ref="deleteModal" type="red">
      <template #title>Remove Sale Upload?</template>
      <template #content>
        <p>This sale upload will be deleted from the list.</p>
      </template>
      <template #footer>
        <ez-button @click="closeDeleteModal" type="link">Cancel</ez-button>
        <ez-button @click="deleteSaleUpload" type="red">Remove</ez-button>
      </template>
    </ez-confirmation-modal>
    <ez-csv-upload-modal
      ref="csvUploadModal"
      :uploadCSV="uploadSalesCSV"
      :onUploadFinished="onUploadFinished"
      venue-sales
      :row-update-allowed="false"
      :row-entity-name="`Sales`"
      :templateUrl="`venue/sales/import-template`"
      :showDateRangePicker="true"
      dateRangePickerTitle="Select the date range covered by this sales data."
      :showSpecial="true"
      :specialHeader="`Menu items without sales data`"
    />
  </div>

</template>

<style scoped lang="scss">
.all-products__actions {
  display: flex;

  :deep() .dropdown-filters {
    .input-group {
      margin: 0;
    }

    .filters-area__row:not(:first-child) {
      justify-content: start;
    }
  }

  :deep() .ez-filter-list {
    &__items {
      margin: 0;
    }
  }
}

.export-actions {
  position: relative;
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 16px;
}

.status-checkbox {
  @include font-size(14px, 20px);
}

.select-supplier {
  :deep() .select-search__trigger {
    width: 100%;
    color: $color-gray-6C;
  }

  :deep() .select-search__list {
    min-height: 20rem;
  }
}

.calendar {
  :deep() .date-picker {
    .ez-simple-dropdown__display-container {
      width: 100%;
    }
    .placeholder {
      color: $color-gray-6C;
    }
  }
}
.product-info {
  display: flex;
  flex-direction: column;

  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .product-info-secondary {
    &--category {
      color: $color-gray-25;
      font-weight: 600;
    }
  }
}

@media (max-width: 1023px) {
  .percent {
    margin-left: 12px;
  }
}

:deep() .export-dropdown {
  margin-left: auto;
  width: auto;
  display: inline-block;

  .ez-simple-dropdown__label {
    margin-right: 16px;
  }
  .ez-simple-dropdown__dropdown {
    width: max-content;
  }
}

.item-drawer {
  &__header {
    display: flex;
    justify-content: space-between;
    border-bottom: 1px dashed #dee1e4;
  }
}

.product-image {
  @include size(120px);
  object-fit: cover;
  object-position: center;
  border: 1px solid #dee1e4;
  border-radius: 6px;
}

.product-title {
  margin: 0;
  font-size: 24px;
  font-weight: 500;
  line-height: 32px;
}

.product-price {
  margin-top: 8px;
  color: $color-primary-blue;
  font-size: 18px;
  letter-spacing: -0.5px;
  line-height: 24px;

  :deep() .v-price__price {
    font-size: 18px;
    letter-spacing: -0.5px;
    line-height: 24px;
    font-weight: 500;
  }

  :deep() .v-price__unit--row {
    font-weight: 400;
  }
}

.contact-info__item {
  margin-left: 24px;
  margin-right: 24px;
  padding: 16px 0px;
  border-bottom: 1px dashed #dee1e4;

  :deep() .label {
    font-size: 12px;
    line-height: 18px;
    margin-bottom: 4px;
  }

  :deep() .contact-info__value {
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
  }
}

.stats-label {
  line-height: 18px;
}

:deep() .stats-box__value {
  font-size: 20px;
  line-height: 28px;
}

.table-title {
  font-size: 16px;
  line-height: 22px;
  font-weight: 600;
  letter-spacing: -0.5px;
}

:deep() .table tbody tr td.order-number-cell {
  font-size: 16px;
  line-height: 22px;
  font-weight: 600;
  color: $color-gray-25;
  .invoice-number {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    &.has-tooltip {
      display: block;
    }
  }
}

:deep() .table .quantity-small-cell {
  width: 90px;
}

:deep() .product-group-filter .ez-select__display-container {
  height: 2.25rem;
}

:deep() .empty-state-center {
  display: flex;
  flex-direction: column;
  align-items: center;
}

:deep() .calendar .date-picker .placeholder {
  min-width: 128px;
}
</style>
